'use client';

import { Fragment } from 'react';
import Link from 'next/link';
import styles from './QualityFinish.module.scss';
import { gaCta } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

interface Checkpoint {
  title: string;
  description: string;
}

interface QualityFinishProps {
  eyebrow?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  checkpoints?: Checkpoint[];
  ctaLabel?: string;
  ctaHref?: string;
  backgroundImage?: string;
  tilt?: 'left' | 'right';
}

const DEFAULT_CHECKPOINTS: Checkpoint[] = [
  {
    title: 'Audit & périmètre validés',
    description: 'Avant le premier commit, le scope, les KPIs et la stack sont alignés.',
  },
  {
    title: 'Design & architecture approuvés',
    description: 'Wireframes, maquettes et architecture technique validés avec vous.',
  },
  {
    title: 'Code testé, typé et documenté',
    description: 'Tests automatisés, typage strict, revues de code et documentation claire.',
  },
  {
    title: 'Déploiement & automatisations',
    description: 'CI/CD, monitoring et premières automations opérationnelles dès le lancement.',
  },
  {
    title: 'Métriques & itérations post-launch',
    description: 'Dashboards partagés et itérations data-driven pour une amélioration continue.',
  },
];

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const renderTitle = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

export function QualityFinish({
  eyebrow = 'FINITION EXIGEANTE',
  title = 'Un projet parfaitement fini,',
  highlightedTitle = '*avec une qualité très haute*',
  description = 'Nous ne livrons pas un MVP bancal. Chaque livrable passe par des contrôles qualité stricts pour être pérenne, performant et maintenable.',
  checkpoints = DEFAULT_CHECKPOINTS,
  ctaLabel = 'Voir nos standards qualité',
  ctaHref = '/expertise',
  backgroundImage,
  tilt = 'left',
}: QualityFinishProps) {
  useAOS();

  const tiltClass = tilt === 'left' ? styles.tiltLeft : styles.tiltRight;
  const hasBgClass = backgroundImage ? styles.hasBg : '';

  return (
    <div
      className={`${styles.root} ${tiltClass} ${hasBgClass}`}
      style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
    >
      <div className={styles.bg} aria-hidden="true" />
      <div className={styles.inner}>
        <div className={styles.text} data-aos="fade-up" data-aos-duration="700">
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          <h2 className={styles.title}>
            {renderTitle(title)}{' '}
            <span className={styles.highlight}>{renderTitle(highlightedTitle)}</span>
          </h2>
          <p className={styles.description}>{description}</p>
          {ctaLabel && ctaHref && (
            <Link
              href={ctaHref}
              className={styles.cta}
              onClick={() => gaCta(ctaLabel, 'quality_finish_cta', ctaHref)}
            >
              {ctaLabel}
            </Link>
          )}
        </div>

        <div
          className={styles.card}
          data-aos="fade-up"
          data-aos-duration="900"
          data-aos-delay="150"
        >
          <div className={styles.seal}>
            <CheckIcon />
          </div>
          <h3 className={styles.cardTitle}>Qualité certifiée</h3>
          <p className={styles.cardLead}>
            Chaque projet est validé avant livraison selon nos 5 critères de finition.
          </p>

          <ul className={styles.checklist}>
            {checkpoints.map((item, i) => (
              <li
                key={item.title}
                className={styles.item}
                data-aos="fade-up"
                data-aos-duration="600"
                data-aos-delay={200 + i * 80}
              >
                <span className={styles.check}>
                  <CheckIcon />
                </span>
                <div>
                  <strong>{item.title}</strong>
                  <span>{item.description}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
